import React, { useState } from 'react';
import { Box, IconButton, Link, Tooltip, Typography } from '@mui/material';
import { LocalShipping } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useFeatureFlagEnabled, usePostHog } from 'posthog-js/react';
import { AccountData, HolidayCalendar, OrderData, OrderService } from '../../api';
import { UseAuthPermissions } from '../../session/UseAuthPermissions';
import { useSafeExecute } from '../../common/apiNotifications/safeExecute';
import ConfirmationDialog from '../../common/ui/ConfimationDialog';

enum CommunicationMethod {
  WHATSAPP = 'WhatsApp',
  SMS = 'SMS',
}

type PickupNotificationButtonProps = {
  order: OrderData;
  account: AccountData;
  onOrderUpdate: (updatedOrder: OrderData) => Promise<void>;
  compact?: boolean;
};

const PickupNotificationButton = ({
  order,
  account,
  onOrderUpdate,
  compact = false,
}: PickupNotificationButtonProps) => {
  const { t } = useTranslation();
  const { isAtLeastSupervisor } = UseAuthPermissions();
  const safeExecute = useSafeExecute();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const posthog = usePostHog();
  const pickupFeatureEnabled = useFeatureFlagEnabled('feature-pickup-ready');

  const communicationMethod =
    account?.holiday_calendar === HolidayCalendar.ISR ? CommunicationMethod.WHATSAPP : CommunicationMethod.SMS;

  const handleConfirmNotification = async () => {
    await safeExecute({
      apiCall: () => OrderService.notifyPickup(order.id),
      successMessage: t('pickup_notification_sent'),
    });
    posthog.capture('Pickup notification sent', {
      orderId: order.id,
      orderNumber: order.order_number,
    });
    await onOrderUpdate({ ...order, pickup_notification_sent: true });
    setConfirmDialogOpen(false);
  };

  const confirmationText = t('pickup_notification_confirmation', {
    customerName: `${order.firstname} ${order.lastname}`,
    orderNumber: order.order_number,
    methods: communicationMethod,
  });

  const isWhatsapp = communicationMethod === CommunicationMethod.WHATSAPP;
  const confirmationContent = (
    <Box>
      <Typography>{confirmationText}</Typography>
      <Box mt={2}>
        <Typography variant="caption" color="text.secondary">
          {t('pickup_notification_settings_links')}:{' '}
          <Link
            href={isWhatsapp ? '/dashboard/whatsapp-settings' : '/dashboard/sms-templates'}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: 'text.secondary', fontSize: 'inherit' }}>
            {isWhatsapp ? t('pickup_notification_whatsapp_settings') : t('pickup_notification_sms_settings')}
          </Link>
        </Typography>
      </Box>
    </Box>
  );

  if (!pickupFeatureEnabled) {
    return null;
  }

  return (
    <>
      <Tooltip
        title={
          order.pickup_notification_sent
            ? t('pickup_notification_sent')
            : !isAtLeastSupervisor
            ? t('noPermissions')
            : t('notify_pickup_ready')
        }
        arrow>
        <span>
          <IconButton
            style={compact ? { padding: '0px' } : undefined}
            disabled={!isAtLeastSupervisor || order.pickup_notification_sent}
            sx={{
              '&.Mui-disabled': {
                cursor: 'not-allowed !important',
              },
            }}
            onClick={() => setConfirmDialogOpen(true)}>
            <LocalShipping sx={order.pickup_notification_sent ? { color: 'primary.main' } : undefined} />
          </IconButton>
        </span>
      </Tooltip>

      <ConfirmationDialog
        isOpen={confirmDialogOpen}
        onClose={async (confirmed) => {
          if (confirmed) {
            await handleConfirmNotification();
          } else {
            setConfirmDialogOpen(false);
          }
        }}
        title={t('pickup_notification_confirm_title')}
        content={confirmationContent}
        ctaText={t('pickup_notification_send')}
        confirmButtonColor="primary"
      />
    </>
  );
};
export default PickupNotificationButton;
